// src/api/utils/errorHandler.ts

import { track } from "../../utils/analytics";
import { triggerAuthRefreshIfRegistered } from "../../auth/authRefreshBridge";
import { ChatApiError } from "../chatApi";
import { ChatError } from "../../interfaces/chatTypes";

/**
 * Emit a PostHog event when the backend rejects a request, and — only on 401 —
 * ask AuthContext (via the refresh bridge) to refresh the token.
 *
 * Why the 401/403 split:
 *   - 401 ("Unauthorized") per RFC 7235 means the credential is missing or
 *     invalid. A token refresh can plausibly fix it (expired JWT, rotated
 *     audience, user-not-yet-provisioned-in-DB on first login).
 *   - 403 ("Forbidden") means the credential IS valid but the principal lacks
 *     permission for the resource. Refreshing produces the same token with
 *     the same claims — it cannot fix the authorization decision. Firing a
 *     refresh here just burns the circuit breaker's 3-per-30s budget and can
 *     mask a legitimate 401 later.
 *
 * We still capture both 401 and 403 in PostHog so an auth-layer regression
 * that flips to 403 is still visible in dashboards.
 *
 * The refresh call is fire-and-forget — the current response still bubbles
 * up to the caller. Subsequent API calls benefit from the refreshed token.
 */
export function handleAuthRejection(response: Response): void {
  if (response.status !== 401 && response.status !== 403) return;
  try {
    track("auth:rejected_by_backend", {
      status: response.status,
      // URL may contain query params; keep the path only to keep payload small.
      endpoint: new URL(response.url).pathname,
    });
  } catch {
    // Never let telemetry failure propagate (new URL() can throw on odd URLs).
  }
  // Only 401 is plausibly fixable by a fresh token.
  if (response.status === 401) {
    triggerAuthRefreshIfRegistered();
  }
}

/**
 * Parse error response from backend API
 *
 * This utility function standardizes error response parsing across all API clients.
 * It follows the same pattern established in chatApi.ts and executedContractsApi.ts
 *
 * @param response The HTTP response object from fetch
 * @returns Promise<ChatError> Parsed error data or fallback error object
 */
export async function parseErrorResponse(response: Response): Promise<ChatError> {
  handleAuthRejection(response);
  try {
    const errorData = (await response.json()) as ChatError;
    // The HTTP status on the wire is authoritative - the body's claim can be
    // absent (FastAPI's default `{"detail": ...}` shape) or plain wrong (the
    // backend's generic HTTPException handler embeds its 500/INTERNAL_ERROR
    // defaults in the envelope even on a 400/401 response). Downstream capture
    // logic branches on this field (4xx vs 5xx), so always take the wire value.
    return { ...errorData, status_code: response.status };
  } catch {
    // Fallback if response is not valid JSON
    // This ensures we always have a properly structured error even if the backend
    // returns malformed JSON or non-JSON error responses
    return {
      status_code: response.status,
      error_code: "HTTP_ERROR",
      message: `HTTP ${response.status}: ${response.statusText}`,
      request_id: "unknown",
      timestamp: new Date().toISOString(),
    };
  }
}

/**
 * Convert parsed ChatError to ChatApiError for callback handlers
 *
 * This utility bridges the gap between the backend error format (ChatError)
 * and the error format expected by our streaming callbacks (ChatApiError)
 *
 * @param error The parsed ChatError from the backend
 * @returns ChatApiError instance ready for callback handlers
 */
export function createApiError(error: ChatError): ChatApiError {
  return new ChatApiError(
    error.message || `HTTP ${error.status_code}`,
    error.error_code || "API_ERROR",
    error.status_code,
    error.request_id
  );
}

/**
 * Handle non-OK response and convert to ChatApiError
 *
 * This is a convenience function that combines parsing and error creation
 * for use in API implementations that use callbacks
 *
 * @param response The HTTP response object from fetch
 * @returns Promise<ChatApiError> Ready-to-use error for callbacks
 */
export async function handleApiErrorResponse(response: Response): Promise<ChatApiError> {
  const parsedError = await parseErrorResponse(response);
  return createApiError(parsedError);
}
